/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { TABLE_ORDER_COLUMNS } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getOrdersListSelector,
	getIsOrdersListLoadingSelector,
	getPaginationSelector,
	getModesSelector,
	getIsModesLoadingSelector,
	getGroupsSelector,
	getIsGroupsLoadingSelector,
	getIsDraftSelector,
} from './selectors';
import { getGroupsAction, getModesAction, getOrdersListAction, resetStateAction } from './actions';
import { getIsAvailableOrdersButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { getMenuByRules } from './utils';

const OrdersContainerWrapper = ({
	onGetOrdersList,
	ordersList,
	isOrdersListLoading,
	pagination,
	onResetState,
	isAvailable,
	onGetModes,
	modes,
	isModesLoading,
	onGetGroups,
	groups,
	isGroupsLoading,
	isDraft,
}) => {
	useInjectSaga({ key: 'ordersContainerSaga', saga });
	useInjectReducer({ key: 'ordersContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetOrdersList({ searchQuery: removeModalQuery(query.toString()) });
		onGetModes();
		onGetGroups();

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'order');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetOrdersList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetOrdersList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetOrdersList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_ORDER_COLUMNS(modes, groups)));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetOrdersList({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={getMenuByRules()}
			isInnerRouter
			pageContent={
				<DataGridComponent
					headerText={isDraft ? 'Черновики' : 'Заказы'}
					onAddButtonClick={isAvailable.create && !isDraft ? handleAddButtonClick : null}
					columns={TABLE_ORDER_COLUMNS(modes, groups)}
					rows={ordersList}
					pagination={
						!pagination
							? null
							: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
					}
					isTableDataLoading={isOrdersListLoading || isModesLoading || isGroupsLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	ordersList: getOrdersListSelector(),
	isOrdersListLoading: getIsOrdersListLoadingSelector(),
	modes: getModesSelector(),
	isModesLoading: getIsModesLoadingSelector(),
	groups: getGroupsSelector(),
	isGroupsLoading: getIsGroupsLoadingSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableOrdersButtonsSelector(),
	isDraft: getIsDraftSelector(),
});

const mapDispatchToProps = {
	onGetOrdersList: getOrdersListAction,
	onGetModes: getModesAction,
	onGetGroups: getGroupsAction,
	onResetState: resetStateAction,
};

const OrdersContainer = connect(mapStateToProps, mapDispatchToProps)(OrdersContainerWrapper);

OrdersContainerWrapper.propTypes = {
	onGetOrdersList: PropTypes.func.isRequired,
	ordersList: PropTypes.array.isRequired,
	isOrdersListLoading: PropTypes.bool.isRequired,
	onGetModes: PropTypes.func.isRequired,
	modes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	isModesLoading: PropTypes.bool.isRequired,
	onGetGroups: PropTypes.func.isRequired,
	groups: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	isGroupsLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	isDraft: PropTypes.bool.isRequired,
};

export default OrdersContainer;
